import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useMalak } from "@/providers/MalakProvider";
import {
  Package,
  Truck,
  MapPin,
  MessageCircle,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ─── CHAT SIMULATION ─── */
const ChatSimulation = () => {
  const { t } = useTranslation();

  const scenarios = [
    {
      messages: [
        { role: "user" as const, text: t('malakSection.scenarios.tracking.userMsg') },
        { role: "malak" as const, text: t('malakSection.scenarios.tracking.malakMsg') },
      ],
      card: {
        title: t('malakSection.scenarios.tracking.cardTitle'),
        rows: [
          { label: t('malakSection.scenarios.tracking.status'), value: t('malakSection.scenarios.tracking.statusValue') },
          { label: t('malakSection.scenarios.tracking.location'), value: t('malakSection.scenarios.tracking.locationValue') },
          { label: t('malakSection.scenarios.tracking.estimated'), value: t('malakSection.scenarios.tracking.estimatedValue') },
        ],
      },
    },
    {
      messages: [
        { role: "user" as const, text: t('malakSection.scenarios.couriers.userMsg') },
        { role: "malak" as const, text: t('malakSection.scenarios.couriers.malakMsg') },
      ],
      card: {
        title: t('malakSection.scenarios.couriers.cardTitle'),
        rows: [
          { label: t('malakSection.scenarios.couriers.route'), value: "Paris → Casablanca" },
          { label: "", value: "• Ahmed Transport" },
          { label: "", value: "• Atlas Express" },
          { label: "", value: "• Maghreb Logistics" },
          { label: "", value: t('malakSection.scenarios.couriers.moreCouriers', { count: 5 }) },
        ],
      },
    },
    {
      messages: [
        { role: "user" as const, text: t('malakSection.scenarios.createTrip.userMsg') },
        { role: "malak" as const, text: t('malakSection.scenarios.createTrip.malakMsg') },
      ],
      card: {
        title: t('malakSection.scenarios.createTrip.cardTitle'),
        rows: [
          { label: t('malakSection.scenarios.couriers.route'), value: "Paris → Casablanca" },
          { label: t('malakSection.scenarios.createTrip.departure'), value: t('malakSection.scenarios.createTrip.departureValue') },
          { label: t('malakSection.scenarios.tracking.status'), value: t('malakSection.scenarios.createTrip.statusValue') },
        ],
        showCheck: true,
      },
    },
  ];

  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState<number>(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [checkVisible, setCheckVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout[]>([]);

  const clearTimeouts = () => {
    timeoutRef.current.forEach(clearTimeout);
    timeoutRef.current = [];
  };

  const addTimeout = (fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timeoutRef.current.push(id);
  };

  useEffect(() => {
    clearTimeouts();
    setVisibleMessages(0);
    setIsTyping(false);
    setShowCard(false);
    setCheckVisible(false);

    const scenario = scenarios[scenarioIndex];
    let delay = 400;

    addTimeout(() => setVisibleMessages(1), delay);
    delay += 1200;
    addTimeout(() => setIsTyping(true), delay);
    delay += 800;
    addTimeout(() => {
      setIsTyping(false);
      setVisibleMessages(2);
    }, delay);
    delay += 1000;
    addTimeout(() => setShowCard(true), delay);

    if (scenario.card.showCheck) {
      delay += 400;
      addTimeout(() => setCheckVisible(true), delay);
    }

    delay += 3000;
    addTimeout(() => {
      setScenarioIndex((i) => (i + 1) % scenarios.length);
    }, delay);

    return clearTimeouts;
  }, [scenarioIndex]);

  const scenario = scenarios[scenarioIndex];

  return (
    <div className="relative bg-white border border-[var(--color-grey-100)] shadow-xl overflow-hidden w-full max-w-[380px] mx-auto lg:mx-0 rounded-lg" style={{ minHeight: 440 }}>
      <div
        className="flex items-center gap-3 px-5 py-3 border-b border-[var(--color-grey-100)]"
        style={{ background: "var(--color-navy)" }}
      >
        <div className="w-8 h-8 bg-[var(--color-orange)] flex items-center justify-center text-white font-bold text-sm">
          M
        </div>
        <div>
          <p className="text-sm font-bold text-white">Malak</p>
          <p className="text-[10px] text-white/40">{t('malakSection.chatSubtitle')}</p>
        </div>
        <div className="ms-auto w-2 h-2 bg-green-500 rounded-full" />
      </div>

      <div className="p-4 space-y-3 h-[380px] overflow-hidden bg-[var(--color-off-white)]">
        {scenario.messages.map((msg, i) => {
          if (i >= visibleMessages) return null;
          return (
            <div
              key={`${scenarioIndex}-${i}`}
              className={cn(
                "animate-fade-in",
                msg.role === "user" ? "flex justify-end" : "flex justify-start"
              )}
            >
              <div
                className={cn(
                  "px-4 py-2.5 text-sm max-w-[85%] leading-relaxed",
                  msg.role === "user"
                    ? "text-white"
                    : "bg-white border border-[var(--color-grey-100)] text-[var(--color-navy)]"
                )}
                style={msg.role === "user" ? { background: "var(--color-navy)" } : undefined}
              >
                {msg.text}
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="flex justify-start animate-fade-in">
            <div className="bg-white border border-[var(--color-grey-100)] px-4 py-2.5 text-sm text-[var(--color-grey-500)] flex items-center gap-1">
              <span>{t('malakSection.typing')}</span>
              <span className="flex gap-0.5 ml-1">
                <span className="w-1 h-1 bg-[var(--color-grey-300)] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1 h-1 bg-[var(--color-grey-300)] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1 h-1 bg-[var(--color-grey-300)] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
            </div>
          </div>
        )}

        {showCard && (
          <div className="animate-fade-in mx-1">
            <div className="bg-white border border-[var(--color-grey-100)] border-t-2 border-t-[var(--color-orange)] p-4">
              <div className="flex items-center gap-2 mb-3">
                <h4 className="font-display font-bold text-[var(--color-navy)] text-sm">
                  {scenario.card.title}
                </h4>
                {scenario.card.showCheck && checkVisible && (
                  <CheckCircle
                    className="h-4 w-4 text-green-500"
                    style={{ animation: "scaleIn 0.3s ease-out" }}
                  />
                )}
              </div>
              {scenario.card.rows.map((row, i) => (
                <div key={i} className="flex justify-between text-xs py-1">
                  {row.label ? (
                    <>
                      <span className="text-[var(--color-grey-500)]">{row.label}</span>
                      <span className="text-[var(--color-navy)] font-medium">{row.value}</span>
                    </>
                  ) : (
                    <span className="text-[var(--color-grey-500)]">{row.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ─── */
const MeetMalak = () => {
  const { t } = useTranslation();
  const { toggle } = useMalak();

  const features = [
    { icon: Package, text: t('malakSection.features.track'), color: "text-blue-500" },
    { icon: Truck, text: t('malakSection.features.manage'), color: "text-green-500" },
    { icon: MapPin, text: t('malakSection.features.findRoutes'), color: "text-purple-500" },
    { icon: MessageCircle, text: t('malakSection.features.getHelp'), color: "text-yellow-500" },
  ];

  return (
    <section className="py-12 md:py-32 bg-white">
      <div className="container max-w-5xl">
        <div className="text-center mb-10 md:mb-20">
          <span className="section-badge-light">{t('malakSection.badge')}</span>
          <h2 className="font-display section-heading text-[var(--color-navy)] mt-6">
            {t('malakSection.heading')}
          </h2>
          <p className="text-[var(--color-grey-500)] mt-4 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            {t('malakSection.subtitle')}
          </p>
          <div className="flex justify-center mt-6">
            <div className="h-[2px] w-[120px] relative overflow-hidden" style={{ background: "var(--color-orange)" }}>
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(242,100,25,0.6), transparent)",
                  animation: "accentGlow 2s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20 items-center">
          <ChatSimulation />

          <div>
            <p className="text-[var(--color-grey-500)] text-base leading-relaxed mb-8">
              {t('malakSection.description')}
            </p>

            <div className="space-y-4 mb-8">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[var(--color-grey-100)] flex items-center justify-center">
                    <f.icon className={cn("h-5 w-5", f.color)} strokeWidth={1.7} />
                  </div>
                  <span className="text-sm font-medium text-[var(--color-navy)]">{f.text}</span>
                </div>
              ))}
            </div>

            <div className="mb-8">
              <p className="text-xs text-[var(--color-grey-500)] uppercase tracking-wider font-semibold mb-3">
                {t('malakSection.availableOn')}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  {
                    label: "WhatsApp",
                    color: "#25D366",
                    icon: (
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.43 9.43 0 1 1 7.99 4.42zm8.03-17.46A11.36 11.36 0 0 0 12.05.7C5.8.7.7 5.8.7 12.06c0 2 .52 3.95 1.52 5.67L.6 23.3l5.7-1.5a11.34 11.34 0 0 0 5.74 1.47h.01c6.26 0 11.36-5.1 11.36-11.36 0-3.03-1.18-5.89-3.33-8.03z"/>
                      </svg>
                    ),
                  },
                  {
                    label: "Messenger",
                    color: "#0084FF",
                    icon: (
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d="M12 .5C5.52.5.5 5.25.5 11.66c0 3.35 1.37 6.25 3.61 8.25.19.17.3.4.31.65l.06 2.05c.02.65.69 1.08 1.29.81l2.28-1.01c.19-.08.41-.1.61-.04 1.05.29 2.16.44 3.34.44 6.48 0 11.5-4.75 11.5-11.15S18.48.5 12 .5zm6.9 8.58-3.38 5.36a1.72 1.72 0 0 1-2.49.46l-2.69-2.02a.69.69 0 0 0-.83 0l-3.63 2.76c-.48.37-1.12-.21-.79-.72l3.38-5.36a1.72 1.72 0 0 1 2.49-.46l2.69 2.02c.25.18.58.18.83 0l3.63-2.76c.48-.37 1.12.21.79.72z"/>
                      </svg>
                    ),
                  },
                  {
                    label: t('malakSection.liveChat'),
                    color: "var(--color-orange)",
                    icon: <MessageCircle className="h-4 w-4" strokeWidth={2} aria-hidden="true" />,
                  },
                ].map((c) => (
                  <span
                    key={c.label}
                    className="inline-flex items-center gap-2 border border-[var(--color-grey-200)] bg-transparent px-3 py-2 text-xs font-semibold text-[var(--color-navy)]"
                  >
                    <span style={{ color: c.color }} className="flex">{c.icon}</span>
                    {c.label}
                  </span>
                ))}
              </div>
            </div>

            <Button
              onClick={toggle}
              className="w-full border-2 border-[var(--color-navy)] bg-[var(--color-navy)] text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy-mid)] font-bold h-12 px-8 text-base shadow-lg"
            >
              {t('malakSection.askMalak')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes accentGlow {
          0%, 100% { opacity: 0.3; transform: translateX(-100%); }
          50% { opacity: 1; transform: translateX(100%); }
        }
      `}</style>
    </section>
  );
};

export default MeetMalak;
